import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Dropdown, Layout, Menu, MenuProps, Select } from 'antd';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { MenuItemType } from 'types';
import { menuItems, sidebarItems, sidebarItemsConst } from 'constant';
import { Buildings, CaretDown, GearSix, SignOut, User, Warehouse } from '@phosphor-icons/react';
import { SubMenu, Breadcrumb, AnimationCommon } from 'components/common';
import { AnimatePresence } from 'framer-motion';
import { logo_cms } from 'utils/importFile';
import useAuthContext from 'stores/auth-context';
import { filterMenuByPermission } from 'utils/permission';
import { useWarehouseContext } from 'stores/warehouseContext';
import { useActiveWarehouseMutation } from 'services';

const { Header, Content } = Layout;
interface AntdMenuItem {
  key: string;
  icon?: React.ReactNode;
  label: React.ReactNode;
  children?: AntdMenuItem[];
}

const Navbar: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [activeMenuKey, setActiveMenuKey] = useState<string>('');
  const [hoveredMenuKey, setHoveredMenuKey] = useState<string | null>(null);
  const [hoveredSetting, setHoveredSetting] = useState(false);
  const [submenuPosition, setSubmenuPosition] = useState<{ left: number; width: number } | null>(null);
  const menuItemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const settingRef = useRef<HTMLDivElement>(null);
  const warehouseRef = useRef<HTMLDivElement>(null);
  const { profile, onSignOut } = useAuthContext();
  const { warehouses, activeWarehouse, setActiveWarehouse } = useWarehouseContext();
  const [activeWarehouseApi] = useActiveWarehouseMutation();

  const handleClick = useCallback(
    (e: { key: string }) => {
      setActiveMenuKey(e.key);
      const findPath = (items: MenuItemType[]): string | undefined => {
        for (const item of items) {
          if (item.key === e.key) return item.path;
        }
        return undefined;
      };
      const path = findPath(menuItems);
      if (path) navigate(path);
    },
    [navigate],
  );
  const items: MenuProps['items'] = [
    {
      key: '1',
      label: 'Đăng xuất',
      icon: <SignOut size={20} />,
      onClick: () => onSignOut(),
      style: { color: 'red', backgroundColor: 'transparent' },
    },
  ];
  const findActiveKey = useCallback(
    (items: MenuItemType[]): string => {
      let bestMatch = '';
      let bestMatchLength = 0;
      for (const item of items) {
        const currentPath = item.path || '';

        if (currentPath && location.pathname.startsWith(currentPath)) {
          if (currentPath.length > bestMatchLength) {
            bestMatch = item.key;
            bestMatchLength = currentPath.length;
          }
        }
      }
      return bestMatch;
    },
    [location.pathname],
  );
  useEffect(() => {
    setActiveMenuKey(findActiveKey(menuItems));
  }, [findActiveKey]);

  const handleMenuMouseEnter = useCallback((key: string) => {
    setHoveredMenuKey(key);

    const menuItemEl = menuItemRefs.current[key];
    const containerEl = containerRef.current;
    if (!menuItemEl?.children) {
      return;
    }
    if (menuItemEl && containerEl) {
      const menuRect = menuItemEl.getBoundingClientRect();
      const containerRect = containerEl.getBoundingClientRect();

      // calculate the position of the submenu
      setSubmenuPosition({
        left: menuRect.left - containerRect.left - 8,
        width: menuRect.width,
      });
    }
  }, []);
  const handleSettingMouseEnter = useCallback(() => {
    setHoveredSetting(true);
  }, []);
  const handleMouseLeave = useCallback(() => {
    setHoveredSetting(false);
  }, []);

  const handleChange = async (warehouseId: string) => {
    await activeWarehouseApi(warehouseId).unwrap();
    setActiveWarehouse(warehouseId);
    window.location.reload();
  };

  const handleMenuMouseLeave = useCallback(() => {
    setHoveredMenuKey(null);
  }, []);

  const renderMenuItems = useCallback(
    (items: MenuItemType[]): AntdMenuItem[] =>
      items.flatMap((item) => {
        if (!item?.isHide) {
          const hasChildren = item.children && item.children.length > 0;

          return {
            key: item.key,
            label: (
              <div
                ref={(el) => {
                  menuItemRefs.current[item.key] = el;
                }}
                onMouseEnter={() => handleMenuMouseEnter(item.key)}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {item.label}
                  {hasChildren && <CaretDown size={18} />}
                </span>
              </div>
            ),
            children: undefined,
          };
        }
        return [];
      }),
    [handleMenuMouseEnter],
  );
  const hoveredMenuItem = hoveredMenuKey
    ? filterMenuByPermission(menuItems, profile?.data?.permissionKeys || [])?.find(
        (item) => item.key === hoveredMenuKey,
      )
    : null;

  return (
    <div className='sticky top-0 z-50' ref={containerRef} onMouseLeave={handleMenuMouseLeave}>
      <Header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
        }}
        className='px-[24px] bg-[var(--opacityTextColor)] py-[12px] gap-[24px]'
      >
        <div className='hidden lg:flex text-[var(--textWhite)] font-bold text-[20px] leading-[100%] w-[150px]'>
          <img src={logo_cms} alt='logo' />
        </div>
        <Menu
          theme='dark'
          mode='horizontal'
          onClick={handleClick}
          selectedKeys={[activeMenuKey]}
          items={
            renderMenuItems(
              filterMenuByPermission(menuItems, profile?.data?.permissionKeys || []),
            ) as unknown as MenuProps['items']
          }
          className='bg-[var(--opacityTextColor)] 
          text-[16px] 
          font-[400] 
          transition-all ease-in-out
          flex-1 !outline-none 
          header-menu
          lg:max-w-[calc(100%-400px)]'
        />
        <div className='flex justify-end items-center gap-[8px]'>
          {/* <div
            ref={warehouseRef}
            className='box-input w-[200px] bg-[var(--hoverColor)] px-[8px] py-[4px] rounded-[8px]'
          >
            <div className='flex items-center gap-[8px]'>
              <div className='avatar w-[32px] h-[32px] p-[8px] rounded-[8px] bg-[var(--textWhite8)]'>
                <Buildings size={18} color='var(--textWhite)' />
              </div>
              <div className='role text-[14px] font-[500] leading-[20px] text-[var(--textWhite)]'>
                {warehouses.find((w) => w.id === activeWarehouse?.id)?.name || 'Kho'}
              </div>
            </div>
          </div> */}
          <div ref={warehouseRef} className='box-input w-[200px]  rounded-[8px]'>
            <Select
              value={activeWarehouse?.id}
              onChange={handleChange}
              options={warehouses.map((w) => ({
                label: (
                  <div className='custom-options flex items-center gap-[8px]'>
                    <div className='avatar  rounded-[8px] bg-[var(--textWhite8)]'>
                      <Buildings size={18} color='var(--textWhite)' />
                    </div>
                    <div className='role text-[14px] font-[500] leading-[20px] text-[var(--textWhite)]'>{w.name}</div>
                  </div>
                ),
                value: w.id,
              }))}
              className='custom-warehouse-select w-full bg-[var(--hoverColor)]'
              suffixIcon={null}
              optionLabelProp='label'
              dropdownClassName='custom-warehouse-dropdown'
            />
          </div>
          {/* <div className='cursor-pointer w-[40px] h-[40px] flex justify-center items-center'>
            <Badge offset={[-5, 0]} dot size='default' styles={{ indicator: { boxShadow: 'none' } }}>
              <Bell size={24} color='var(--textWhite)' />
            </Badge>
          </div> */}

          <div
            ref={settingRef}
            onMouseEnter={handleSettingMouseEnter}
            onMouseLeave={handleMouseLeave}
            className='cursor-pointer w-[56px] h-[64px] flex justify-center items-center relative'
          >
            <Link to='/settings'>
              <div className='w-[56px] h-[40px] flex justify-center items-center bg-[var(--textWhite12)] rounded-full'>
                <GearSix size={24} color='var(--textWhite)' />
              </div>
            </Link>

            <AnimatePresence mode='wait'>
              {hoveredSetting && sidebarItems && (
                <AnimationCommon
                  key='settings-submenu'
                  effect='fadeDown'
                  transition='spring'
                  disablePresence={true}
                  duration={0.3}
                  className='!absolute z-50 top-[64px] transform -translate-x-1/2 !leading-[20px]'
                  style={{ minWidth: '200px' }}
                >
                  <SubMenu
                    menuItem={{
                      key: 'quanLyCaiDat',
                      label: 'Quản Lý Cài Đặt',
                      path: '/quan-ly-cai-dat',
                      icon: <Warehouse size={20} />,
                      children: filterMenuByPermission(
                        [sidebarItems],
                        profile?.data?.permissionKeys || [],
                      ) as MenuItemType[],
                    }}
                    isVisible={hoveredSetting}
                  />
                </AnimationCommon>
              )}
            </AnimatePresence>
          </div>
          <Dropdown menu={{ items }} placement='bottomLeft' arrow>
            <div className='cursor-pointer flex items-center'>
              <div className='w-[40px] h-[40px] flex justify-center items-center'>
                <User size={18} color='var(--textWhite)' />
              </div>
              <span className='' style={{ color: 'white' }}>
                {profile?.data.baseUser.name}
              </span>
            </div>
          </Dropdown>
        </div>
      </Header>
      <AnimatePresence mode='wait'>
        {hoveredMenuItem &&
          hoveredMenuItem?.children &&
          hoveredMenuItem?.children?.length > 0 &&
          hoveredMenuKey &&
          submenuPosition && (
            <AnimationCommon
              key={hoveredMenuKey}
              effect='fadeDown'
              transition='spring'
              duration={0.3}
              disablePresence={true}
              className='absolute z-50 top-[64px]'
              style={{
                left: `${submenuPosition.left}px`,
                width: `${submenuPosition.width}px`,
              }}
            >
              <SubMenu menuItem={hoveredMenuItem} isVisible={hoveredMenuKey === hoveredMenuItem.key} />
            </AnimationCommon>
          )}
      </AnimatePresence>

      <Content>
        <Breadcrumb menuItems={[...menuItems, sidebarItems, ...sidebarItemsConst]} />
      </Content>
    </div>
  );
};

export default Navbar;
